// OVERHANG_ROW_COUNT specifies how many rows should be rendered on each side outside of the visible area
// there has to be some rows for the TABbing to work correctly
// if there isn't row rendered in the DOM, browser can't jump to it when tabbing
import { IProps as IIconProps } from "@components/icon";
import { IRowProps } from "@components/table/Rows.types";
import { WithContextMenuProps } from "@components/table/withContextMenu";
import { ITooltipProps } from "@components/tooltip";
import React from "react";

import {
    ActionState,
    BorderSize,
    RowAction,
    RowType,
    Sort,
    Status,
    TableAddingRowType,
    TextAlign,
    ToggleState
} from "../../enums";
import { IModifierKeys, IToString } from "../../global.types";

export const OVERHANG_ROW_COUNT = 2;
export const NEW_ROW_ID = "newRow";

export interface ISort {
    id: TId;
    sort: Sort;
}

export type TId = IToString /*| number*/;

export interface ICellValueObject {
    value: React.ReactNode;
    tooltip: string;
    tooltipStatus?: Status;
    // true by default
    onlyShowTooltipWhenChildrenOverflowing?: boolean;
    isDisabled?: boolean;
    beforeContent?: React.ReactNode;
    oppositeAlignmentContent?: React.ReactNode;
    afterContent?: React.ReactNode;
    hoverContent?: React.ReactNode;
}

export type TCellValue = string | number | ICellValueObject;

export interface IColumnResizeEvent {
    id: TId;
    columnIndex: number;
    oldWidth: number;
    width: number;
    // offset from original width
    delta: number;
    // offset from previous resize event
    stepDelta: number;
    // hierarchy level
    level: number;
    end: boolean;
}

export interface ISticky {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
}

export interface IRowValues {
    [key: string]: TCellValue | (() => TCellValue);
}

export interface IRow {
    /** Unique row id used in events and react key prop. */
    id: TId;
    /** Id for testing purposes, can be different from id.toString() */
    dataId?: string;
    /** Used for shared hover state between multiple rows */
    groupId?: TId;
    /** Object with key-value pairs corresponding to the table columns. Value can also be object {value, title}*/
    values: IRowValues;
    /** Object with key-value pairs corresponding to the table columns. Use overflow: visible instead of hidden.
     * Expected use is for some complex custom content. Don't forget to take care of the overflow and text ellipsis when used. */
    allowOverflow?: Record<string, boolean>;
    selected?: boolean;
    sticky?: ISticky;
    /** If used, renders colored line in the left part of the row */
    statusHighlight?: Status;
    statusHighlightTooltip?: ITooltipProps["content"];
    label?: string;
    open?: boolean;
    /** Number of all the rows, for virtualization. Used together with rows prop.
     * Required for all groups.*/
    rowCount?: number;
    type?: RowType;
    originalIndex?: number;
    /** Whether to show caret on the right side of the row (clickable)*/
    drilldown?: boolean | React.ReactElement | ((row: IRow) => React.ReactElement);
    level?: number;
    hierarchy?: string;
    rows?: IRow[];
    tooltip?: string;
    /** Loading state */
    isLoading?: boolean;
    /** Whether to show a small lock in front of the row. */
    isLocked?: boolean;
    isDisabled?: boolean;
    /** Whether to use special "highlighted" background */
    isHighlighted?: boolean;
    isBold?: boolean;
    /** Renders a divider line under the row */
    isDivider?: boolean;
    /** Can be used to store some custom data, passed in onClick event */
    customData?: any;
    content?: any;
    canUnlock?: boolean;

    color?: string;
}

export interface IColumn {
    /** Unique column id used in events and react key prop. */
    id: string;
    label: string;
    info?: string;
    // width of the column
    width?: number;
    // min width of just the content of the column.
    // used automatically for stretchContent columns by TableWithAutoSizedColumns
    // can be smaller than width in case the column label longer than content.
    minWidth?: number;
    textAlign?: TextAlign;
    /** Disables sort for this column */
    disableSort?: boolean;
    /** Whole column rendered on white background */
    isHighlighted?: boolean;
    border?: BorderSize;
    afterContentMinWidth?: number;
    isSticky?: boolean;
    /** Divider lines won't be rendered for cells of this column.
     * So far, only used on SimpleTable, but could be applied to Table as well. */
    ignoreDividers?: boolean;
    /** Makes all text in the column bold.
     * So far, only used on SimpleTable, but could be applied to Table as well. */
    isBold?: boolean;
    hasGreyHeader?: boolean;
    /**
     * Flag to render the cell content of each row to the same width of the max width content for given column.
     * Typically used to align icons based on the widest text of that column. */
    stretchContent?: boolean;
}

export interface IItemsRenderedEvent {
    visibleStartIndex: number;
    visibleStopIndex: number;
}

export interface IMetaColumn {
    label: string;
    columns: TColumn[];
    info?: string;
    isHighlighted?: boolean;
    border?: BorderSize;
    textAlign?: TextAlign;
}

export type TColumn = IColumn | IMetaColumn;

export interface ILoadMoreItemsEvent {
    startIndex: number;
    stopIndex: number;
    group?: TId;
}

export interface IActionRendererArgs {
    actionState: ActionState;
    onClick: (...args: any) => void;
    isDisabled: boolean;
    isMainToggle: boolean;
    rowId?: TId;
    isLocked?: boolean;
    canUnlock?: boolean;
}

export interface IRowAction {
    actionType: RowAction;
    /** All following props are optional, when not provided, default behavior is used (managed by
     * SmartODataTableBase or SmartReportTable. E.g. customRender is provided or checkboxes are rendered.
     * Tables also can manage selecting particular rows, etc...
     **/
    toggleState?: ToggleState | (() => ToggleState);
    onToggleChange?: (toggleState: ToggleState) => void;
    onClick?: (rowId: TId, row?: IRowProps) => void;
    getActionState?: (rowId: TId, row: IRowProps) => ActionState;
    render?: (args: IActionRendererArgs) => React.ReactNode;
    isSingleSelect?: boolean;
    showIconsOnHover?: ((rowId: TId, row: IRowProps) => boolean) | boolean;
    /** Custom tooltip shown on action hover */
    tooltip?: (rowId: TId) => string;
}

interface IScrollEvent {
    scrollTop: number;
    scrollLeft: number;
}

export interface IRowAddEvent {
    siblingRow: IRow;
    type: TableAddingRowType;
    parent: IRow;
}

export interface ICustomTableColors {
    rowBackgroundColor?: string;
    rowBorderColor?: string;
}

export interface ITableProps extends WithContextMenuProps {
    tableId?: string;
    testId?: string;
    /**
     * Rows of the table. Can be of different row types.
     */
    rows: IRow[];
    columns: TColumn[];
    /** Number of all the rows in first level, for virtualization. Used together with rows prop.
     * if rowCount not given, length of the rows array is used instead (table doesn't expect that more rows than the given rows exists)*/
    rowCount?: number;
    selectedRows?: IToString[];
    minimumBatchSize?: number;
    minimumRenderedRows?: number;
    style?: React.CSSProperties;
    onLoadMoreItems?: (props: ILoadMoreItemsEvent) => void;
    onSortChange?: (props: ISort[]) => void;
    onGroupToggle?: (id: TId) => void;
    /** Disables sort for all columns */
    disableSort?: boolean;
    onScroll?: (props: IScrollEvent) => void;
    /** Disables resize cursor change and functionality. Used in Dashboard tables.  */
    disableColumnResize?: boolean;
    onColumnResize?: (props: IColumnResizeEvent) => void;
    onRowSelect?: (id: TId, props: IRowProps, modifiers?: IModifierKeys) => void;
    onRowContextMenuSelection?: (id: TId, props: IRowProps, modifiers?: IModifierKeys) => void;
    onDragStart?: (id: TId, props: IRowProps, event: React.DragEvent<HTMLDivElement>) => void;
    sort?: ISort[];
    passRef?: React.Ref<HTMLDivElement>;
    tableWrapperRef?: React.Ref<HTMLDivElement>;
    noDataText?: string;
    hasSimplifiedNoData?: boolean;
    addingRow?: TableAddingRowType;
    /* Can be used with any addingRow type, new row will be add as child of this row. If null, new row will be insert as first row. */
    addingRowParent?: TId;
    onAddingRowCancel?: () => void;
    /** Event for the original designs of adding new hierarchical rows */
    onRowAdd?: (args: IRowAddEvent) => void;
    disableVirtualization?: boolean;
    // if explicitly set to false, there won't be additional 6px padding for the status token on the left
    useStatusHighlight?: boolean;
    isForPrint?: boolean;
    rowAction?: IRowAction;
    /** row state icon in front of the row (in front if action if both are active at same time) */
    rowIcon?: (id: TId, row: IRowProps, rowAction: IRowAction) => React.ComponentType<IIconProps>;
    contentBefore?: (id: TId, row: IRowProps) => React.ReactElement;
    hierarchy?: string;
    isList?: boolean;
    isHoverDisabled?: boolean;
    isSkeleton?: boolean;
    customColors?: ICustomTableColors;
    customNewRowLabel?: React.ReactNode;
    customBusyContent?: React.ReactNode;
    withoutShadows?: boolean;
    busy?: boolean;
    disableVerticalScroll?: boolean;
}